import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ onFiltersChange, isCollapsed = false, onToggleCollapse }) => {
  const [filters, setFilters] = useState({
    dateRange: '12months',
    department: 'all',
    assetType: 'all',
    status: 'all',
    costRange: 'all',
    location: 'all'
  });

  const [savedViews, setSavedViews] = useState([
    { id: 'executive', name: 'Executive Summary', isDefault: true },
    { id: 'it-detailed', name: 'IT Detailed View', isDefault: false },
    { id: 'cost-analysis', name: 'Cost Analysis', isDefault: false },
    { id: 'replacement-planning', name: 'Replacement Planning', isDefault: false }
  ]);

  const [currentView, setCurrentView] = useState('executive');

  const dateRangeOptions = [
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '12months', label: 'Last 12 Months' },
    { value: '24months', label: 'Last 24 Months' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'it', label: 'IT Department' },
    { value: 'finance', label: 'Finance' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'sales', label: 'Sales' }
  ];

  const assetTypeOptions = [
    { value: 'all', label: 'All Asset Types' },
    { value: 'laptops', label: 'Laptops' },
    { value: 'desktops', label: 'Desktops' },
    { value: 'servers', label: 'Servers' },
    { value: 'printers', label: 'Printers' },
    { value: 'monitors', label: 'Monitors' },
    { value: 'networking', label: 'Networking Equipment' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'spare', label: 'Spare' },
    { value: 'repair', label: 'Under Repair' },
    { value: 'retired', label: 'Retired' }
  ];

  const costRangeOptions = [
    { value: 'all', label: 'All Cost Ranges' },
    { value: '0-1000', label: '₹0 - ₹1,000' },
    { value: '1000-5000', label: '₹1,000 - ₹5,000' },
    { value: '5000-10000', label: '₹5,000 - ₹10,000' },
    { value: '10000+', label: '₹10,000+' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'building-a', label: 'Building A' },
    { value: 'building-b', label: 'Building B' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'remote', label: 'Remote Locations' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      dateRange: '12months',
      department: 'all',
      assetType: 'all',
      status: 'all',
      costRange: 'all',
      location: 'all'
    };
    setFilters(defaultFilters);
    onFiltersChange?.(defaultFilters);
  };

  const handleSaveView = () => {
    const viewName = prompt('Enter a name for this view:');
    if (viewName) {
      const newView = {
        id: `custom-${Date.now()}`,
        name: viewName,
        isDefault: false,
        filters: { ...filters }
      };
      setSavedViews([...savedViews, newView]);
    }
  };

  const handleLoadView = (viewId) => {
    setCurrentView(viewId);
    const view = savedViews?.find(v => v?.id === viewId);
    if (view && view?.filters) {
      setFilters(view?.filters);
      onFiltersChange?.(view?.filters);
    }
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== 'all' && value !== '12months');

  if (isCollapsed) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 shadow-subtle">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-muted-foreground" />
            <span className="font-medium text-card-foreground">Filters</span>
            {hasActiveFilters && (
              <div className="w-2 h-2 bg-primary rounded-full" />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="h-8 w-8"
          >
            <Icon name="ChevronDown" size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="font-semibold text-card-foreground">Analytics Filters</h3>
          {hasActiveFilters && (
            <div className="w-2 h-2 bg-primary rounded-full" />
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            iconName="RotateCcw"
            disabled={!hasActiveFilters}
          >
            Reset
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="h-8 w-8"
          >
            <Icon name="ChevronUp" size={16} />
          </Button>
        </div>
      </div>
      {/* Saved Views */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-card-foreground">Saved Views</label>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveView}
            iconName="Save"
            className="text-xs"
          >
            Save Current
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {savedViews?.map((view) => (
            <Button
              key={view?.id}
              variant={currentView === view?.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleLoadView(view?.id)}
              className="text-xs"
            >
              {view?.name}
              {view?.isDefault && (
                <Icon name="Star" size={12} className="ml-1" />
              )}
            </Button>
          ))}
        </div>
      </div>
      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Date Range */}
        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
        />

        {/* Department */}
        <Select
          label="Department"
          options={departmentOptions}
          value={filters?.department}
          onChange={(value) => handleFilterChange('department', value)}
        />

        {/* Asset Type */}
        <Select
          label="Asset Type"
          options={assetTypeOptions}
          value={filters?.assetType}
          onChange={(value) => handleFilterChange('assetType', value)}
        />

        {/* Status */}
        <Select
          label="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
        />

        {/* Cost Range */}
        <Select
          label="Cost Range"
          options={costRangeOptions}
          value={filters?.costRange}
          onChange={(value) => handleFilterChange('costRange', value)}
        />

        {/* Location */}
        <Select
          label="Location"
          options={locationOptions}
          value={filters?.location}
          onChange={(value) => handleFilterChange('location', value)}
        />
      </div>
      {/* Custom Date Range (if selected) */}
      {filters?.dateRange === 'custom' && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
          <h4 className="text-sm font-medium text-card-foreground mb-3">Custom Date Range</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              defaultValue="2024-08-19"
            />
            <Input
              label="End Date"
              type="date"
              defaultValue="2025-08-19"
            />
          </div>
        </div>
      )}
      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Active Filters</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters)?.map(([key, value]) => {
              if (value === 'all' || (key === 'dateRange' && value === '12months')) return null;
              
              const label = key?.charAt(0)?.toUpperCase() + key?.slice(1)?.replace(/([A-Z])/g, ' $1');
              const displayValue = value?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase());
              
              return (
                <div
                  key={key}
                  className="inline-flex items-center space-x-1 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-md"
                >
                  <span>{label}: {displayValue}</span>
                  <button
                    onClick={() => handleFilterChange(key, key === 'dateRange' ? '12months' : 'all')}
                    className="hover:bg-primary-foreground/20 rounded-full p-0.5"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;